import {
  At, Barcode, Buildings, Calculator, CalendarBlank, ChatText, CheckSquare, Clock, Cube, CurrencyCircleDollar, Envelope, FileArrowUp, Flag, GridFour, Hash, IdentificationCard,
  Image, Key, Lock, MagnifyingGlass, MapPin, MapTrifold, Money, Motorcycle, Note, Package, Percent, Phone, Scales, Stack, Tag, TextT, Truck, User, UsersThree, Warning,
} from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import type { IconComponent } from './components/primitives'

/**
 * Every form label gets an SVG that says what the field is, derived from the words in the label
 * (Arabic or English) so the hundreds of existing fields pick theirs up without being touched.
 * Order matters: the first matching rule wins, so specific words come before generic ones.
 */
const RULES: [IconComponent, string[]][] = [
  [Barcode, ['awb', 'رقم الشحنة', 'بوليصة', 'بوالص', 'باركود', 'barcode', 'كود', 'code']],
  [Lock, ['كلمة المرور', 'password', 'كلمة السر']],
  [At, ['username', 'اسم المستخدم', 'login']],
  [Key, ['token', 'مفتاح', 'api']],
  [Envelope, ['بريد', 'email', 'إيميل', 'ايميل']],
  [Phone, ['تليفون', 'هاتف', 'موبايل', 'phone', 'mobile', 'رقم العميل']],
  [MapPin, ['عنوان', 'address', 'مدينة', 'city', 'موقع', 'location']],
  [MapTrifold, ['منطقة', 'zone', 'محافظة', 'governorate', 'مناطق', 'sector']],
  [Buildings, ['فرع', 'branch', 'عميل', 'client', 'account', 'حساب', 'شركة', 'company', 'راسل', 'shipper', 'sender']],
  [Motorcycle, ['مندوب', 'runner', 'سائق', 'driver', 'courier']],
  [Truck, ['مركبة', 'vehicle', 'رحلة', 'trip', 'نقل', 'haul']],
  [UsersThree, ['مستخدم', 'user', 'موظف', 'employee', 'دور', 'role']],
  [User, ['اسم', 'name', 'مستلم', 'consignee', 'recipient']],
  [Money, ['تحصيل', 'cod', 'مبلغ', 'amount', 'سعر', 'price', 'رسوم', 'fee', 'فلوس', 'cash']],
  [CurrencyCircleDollar, ['قيمة', 'value', 'تكلفة', 'cost', 'عمولة', 'commission']],
  [Percent, ['نسبة', 'percent', '%']],
  [Scales, ['وزن', 'weight', 'كجم', 'kg']],
  [Stack, ['قطع', 'pieces', 'عدد', 'count', 'كمية', 'quantity', 'qty']],
  [Cube, ['منتج', 'product', 'صنف', 'item', 'sku']],
  [Package, ['محتوى', 'contents', 'شحنة', 'shipment', 'طرد', 'parcel', 'package']],
  [Tag, ['نوع', 'type', 'تصنيف', 'category', 'خدمة', 'service']],
  [Flag, ['حالة', 'status']],
  [Warning, ['سبب', 'reason', 'مشكلة', 'issue']],
  [CalendarBlank, ['تاريخ', 'date', 'يوم', 'day', 'موعد', 'مؤجل', 'من ', 'إلى', 'from', 'to ']],
  [Clock, ['وقت', 'time', 'ساعة', 'hour', 'دقيقة', 'minute', 'مدة', 'duration']],
  [Hash, ['مرجع', 'reference', 'ref', 'رقم', 'number', 'id', 'serial', 'تسلسل']],
  [ChatText, ['تعليمات', 'instruction', 'رسالة', 'message', 'template', 'قالب', 'نص', 'text']],
  [Note, ['ملاحظ', 'note', 'وصف', 'description', 'تعليق', 'comment', 'remark']],
  [MagnifyingGlass, ['بحث', 'search', 'فلتر', 'filter', 'find']],
  [GridFour, ['خلية', 'cell', 'رف', 'rack', 'ممر', 'aisle', 'مخزن', 'store']],
  [FileArrowUp, ['ملف', 'file', 'رفع', 'upload', 'excel', 'شيت', 'sheet']],
  [Image, ['صورة', 'image', 'photo', 'إثبات', 'pod']],
  [IdentificationCard, ['هوية', 'رقم قومي', 'national', 'بطاقة', 'card', 'رخصة', 'license']],
  [CheckSquare, ['مسموح', 'allow', 'تفعيل', 'enable', 'مفعل', 'active']],
  [Calculator, ['حساب', 'calc', 'إجمالي', 'total', 'صافي', 'net']],
]

export function iconForLabel(label: ReactNode): IconComponent {
  if (typeof label !== 'string') return TextT
  const l = label.toLowerCase()
  for (const [icon, words] of RULES) if (words.some((w) => l.includes(w))) return icon
  return TextT
}
